import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const templates = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Design épuré et contemporain',
    colors: ['#2563eb', '#1e40af', '#3b82f6'],
    preview: 'M',
  },
  {
    id: 'professional',
    name: 'Professionnel',
    description: 'Classique et élégant',
    colors: ['#1e3a5f', '#2c5f8d', '#4a90d9'],
    preview: 'P',
  },
  {
    id: 'creative',
    name: 'Créatif',
    description: 'Audacieux et coloré',
    colors: ['#7c3aed', '#8b5cf6', '#a78bfa'],
    preview: 'C',
  },
];

const TemplateSelection = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedColor, setSelectedColor] = useState(templates[0].colors[0]);

  const handleContinue = () => {
    if (selectedTemplate) {
      navigate('/create', { state: { template: selectedTemplate, color: selectedColor } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">CV</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">InstantPro</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Bonjour, {user?.name || user?.email}</span>
            <button
              onClick={logout}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Quel modèle aimez-vous le plus ?
          </h1>
          <p className="text-lg text-gray-600">
            Vous pourrez toujours le modifier plus tard, donc pas de soucis !
          </p>
        </div>

        {/* Color Palette */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Couleur du thème</p>
            <div className="flex space-x-3">
              {templates.flatMap(t => t.colors).filter((color, index, self) => self.indexOf(color) === index).map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                    selectedColor === color ? 'border-gray-900 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {templates.map((template) => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 ${
                selectedTemplate === template.id ? 'ring-4 ring-blue-600' : ''
              }`}
            >
              {/* Template Preview */}
              <div 
                className="h-80 flex items-center justify-center text-white text-6xl font-bold"
                style={{ backgroundColor: selectedColor }}
              >
                {template.preview}
              </div>
              
              {/* Template Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{template.name}</h3>
                  {selectedTemplate === template.id && (
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                <p className="text-gray-600">{template.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            ← Retour
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedTemplate}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continuer →
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelection;
